import "./ts/polyfill";
import "katex/dist/katex.min.css";
import { preLoadCheck } from "./preload";
import { mount } from "svelte";
import { Buffer } from "node:buffer";
import {
  isTauri,
  isTauriMacOS,
  isTauriWindows,
  waitForTauriRuntimeReady,
} from "./ts/platform";
import {
  parseTauriSidebarMenuLaunch,
  readTauriSidebarMenuPopupPayload,
} from "./ts/tauriSidebarMenu";
import {
  applyWindowsTransparencyToDocument,
  initializeWindowsTransparency,
} from "./ts/windowsTransparency";
import { initializeLinuxWindowIntegration } from "./ts/linuxWindowIntegration";
import {
  androidDiagnosticCheckpoint,
  initializeAndroidDiagnostics,
} from "./ts/android/androidCrashDiagnostics";

initializeAndroidDiagnostics();

if (typeof window !== "undefined") {
  window.Buffer = Buffer;
  if (isTauriMacOS) {
    document.documentElement.classList.add("tauri-macos-vibrancy");
  }
  if (isTauriWindows) {
    applyWindowsTransparencyToDocument();
  }
}

window.addEventListener("vite:preloadError", (event) => {
  console.error("Chunk load error detected:", event);
  alert(
    "The server has been updated or the network connection has been lost. Please refresh the page.",
  );
});

async function start() {
  await androidDiagnosticCheckpoint("js:entry");
  if (isTauri && !(await waitForTauriRuntimeReady())) {
    throw new Error("Tauri runtime initialization timed out");
  }
  await Promise.all([
    initializeWindowsTransparency(),
    initializeLinuxWindowIntegration(),
  ]);

  const sidebarMenuLaunch = isTauriMacOS
    ? parseTauriSidebarMenuLaunch(location.search)
    : null;
  if (sidebarMenuLaunch) {
    document.documentElement.classList.add("tauri-sidebar-menu-popup");
    document.getElementById("preloading")?.remove();
    const payload = readTauriSidebarMenuPopupPayload(
      sidebarMenuLaunch.popupWindowLabel,
    );
    if (!payload) {
      const { getCurrentWebviewWindow } =
        await import("@tauri-apps/api/webviewWindow");
      await getCurrentWebviewWindow().close();
      return null;
    }
    const { default: SidebarMenuPopup } =
      await import("./lib/SideBars/SidebarMenuPopup.svelte");
    return mount(SidebarMenuPopup, {
      target: document.getElementById("app"),
      props: { launch: sidebarMenuLaunch, payload },
    });
  }

  const { default: App } = await import("./App.svelte");
  await androidDiagnosticCheckpoint("js:mount");
  preLoadCheck();
  const app = mount(App, {
    target: document.getElementById("app"),
  });
  void Promise.all([import("./ts/bootstrap"), import("./ts/hotkey")]).then(
    ([{ loadData }, { initHotkey }]) => {
      initHotkey();
      return loadData();
    },
  );
  document.getElementById("preloading")?.remove();
  return app;
}

const app = start();
export default app;
